/**
 * Copyright (c) 2024-2026 Danielle Sage. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

import { useState, useRef, useEffect } from "react";
import { supabase } from "./supabase";

// Generates a human-readable summary of already-collected data for context injection on resume
// This prevents Alex re-asking questions that have already been answered
function buildCollectedSummary(data) {
  if (!data || Object.keys(data).length === 0) return null;
  const lines = [];

  // Company
  const co = data.companyName || data.legalName || data.prefilled_companyName;
  if (co) {
    const parts = [co];
    if (data.registrationNumber) parts.push(`Reg: ${data.registrationNumber}`);
    if (data.dateOfIncorporation) parts.push(`Inc: ${data.dateOfIncorporation}`);
    if (data.jurisdiction) parts.push(`Jurisdiction: ${data.jurisdiction}`);
    if (data.registeredAddress) parts.push(`Address: ${data.registeredAddress}`);
    if (data.website) parts.push(`Website: ${data.website}`);
    if (data.businessDescription) parts.push(`Business: ${data.businessDescription.slice(0, 120)}`);
    lines.push('COMPANY: ' + parts.join(' | '));
  }

  // Entity type
  if (data.entityType) lines.push(`ENTITY TYPE: ${data.entityType}`);

  // Directors
  if (data.directors?.length) {
    lines.push('DIRECTORS: ' + data.directors.map(d =>
      typeof d === 'string' ? d : `${d.name}${d.dob ? ' ('+d.dob+')' : ''}${d.role ? ' — '+d.role : ''}${d.status ? ' ['+d.status+']' : ''}`
    ).join('; '));
  }

  // UBOs
  if (data.ubos?.length) {
    lines.push('UBOs: ' + data.ubos.map(u =>
      typeof u === 'string' ? u : `${u.name} ${u.ownership||''}% | ${u.nationality||''} | ${u.countryOfResidence||''} | ${u.email||''}`
    ).join('; '));
  }

  // Authorised rep
  if (data.authorisedRep || data.authorisedRepresentative) {
    const rep = data.authorisedRep || data.authorisedRepresentative;
    lines.push(`AUTHORISED REP: ${typeof rep === 'object' ? `${rep.name} — ${rep.role||''} | ${rep.email||''}` : rep}`);
  }

  // Regulatory
  if (data.regulated !== undefined) {
    const reg = data.regulated ? `YES — ${data.regulator||''} | Licence: ${data.licenceNumber||''} | Type: ${data.licenceType||''} | Status: ${data.licenceStatus||''}` : 'Not regulated';
    lines.push(`REGULATORY: ${reg}`);
  }

  // AML
  const amlParts = [];
  if (data.amlPolicy) amlParts.push(`AML policy: ${data.amlPolicy}`);
  if (data.fraudPolicy) amlParts.push(`Fraud policy: ${data.fraudPolicy}`);
  if (data.mlroName) amlParts.push(`MLRO: ${data.mlroName}${data.mlroEmail ? ' ('+data.mlroEmail+')' : ''}`);
  if (data.mlroPending) amlParts.push('MLRO: Pending — management transition');
  if (data.clientRiskTemplate !== undefined) amlParts.push(`Client risk template: ${data.clientRiskTemplate ? 'Yes' : 'No'}`);
  if (data.externalAudit) amlParts.push(`External audit: ${data.externalAudit}`);
  if (data.enterpriseRiskAssessment !== undefined) amlParts.push(`Enterprise risk assessment: ${data.enterpriseRiskAssessment ? 'Yes' : 'No'}`);
  if (amlParts.length) lines.push('AML & COMPLIANCE: ' + amlParts.join(' | '));

  // Transaction profile
  const txParts = [];
  if (data.annualVolume || data.annualInboundVolume) txParts.push(`Annual volume: ${data.annualVolume || data.annualInboundVolume}`);
  if (data.transactionCount) txParts.push(`Transactions p.a.: ${data.transactionCount}`);
  if (data.avgTransactionSize) txParts.push(`Avg size: ${data.avgTransactionSize}`);
  if (data.currencies?.length) txParts.push(`Currencies: ${Array.isArray(data.currencies) ? data.currencies.join(', ') : data.currencies}`);
  if (data.fundingAccount) txParts.push(`Funding account: ${data.fundingAccount}`);
  if (data.inboundCountries) txParts.push(`Inbound: ${Array.isArray(data.inboundCountries) ? data.inboundCountries.join(', ') : data.inboundCountries}`);
  if (data.outboundCountries) txParts.push(`Outbound: ${Array.isArray(data.outboundCountries) ? data.outboundCountries.join(', ') : data.outboundCountries}`);
  if (data.paymentPurpose) txParts.push(`Purpose: ${data.paymentPurpose}`);
  if (data.equalsCards !== undefined) txParts.push(`Cards: ${data.equalsCards ? 'Yes' : 'Not required'}`);
  if (data.thirdPartyInflows !== undefined) txParts.push(`Third-party inflows: ${data.thirdPartyInflows ? 'Yes — '+( data.thirdPartyInflowDetails||'see notes') : 'None'}`);
  if (txParts.length) lines.push('TRANSACTION PROFILE: ' + txParts.join(' | '));

  // Counterparties
  if (data.counterparties?.length) {
    lines.push('COUNTERPARTIES: ' + (Array.isArray(data.counterparties)
      ? data.counterparties.map(c => typeof c === 'object' ? `${c.name} (${c.country||''}) — ${c.purpose||''}` : c).join('; ')
      : data.counterparties));
  }

  // Client base
  if (data.activeClients) lines.push(`CLIENT BASE: ${data.activeClients} active clients${data.clientSplit ? ' | Split: '+data.clientSplit : ''}${data.clientJurisdictions ? ' | Jurisdictions: '+data.clientJurisdictions : ''}`);
  if (data.highRiskClientTypes) lines.push(`HIGH RISK CLIENT TYPES ACCEPTED: ${data.highRiskClientTypes}`);

  // Outstanding
  if (data.outstandingItems?.length) {
    lines.push('OUTSTANDING (not yet answered): ' + (Array.isArray(data.outstandingItems) ? data.outstandingItems.join('; ') : data.outstandingItems));
  }

  // Documents
  if (data.documentsConfirmed?.length || data.documentsPending?.length) {
    if (data.documentsConfirmed?.length) lines.push('DOCUMENTS CONFIRMED: ' + data.documentsConfirmed.join(', '));
    if (data.documentsPending?.length) lines.push('DOCUMENTS PENDING: ' + data.documentsPending.join(', '));
  }

  if (lines.length === 0) return null;
  return lines.join('\n');
}

// Banking referral Alex system prompt — tuned for Equals requirements
// No FinSA, no Travel Rule, no Chainalysis, no VQF forms
const BANKING_SYSTEM_PROMPT = `You are Alex, the compliance specialist at Sage Capital Management. You are helping prepare a banking referral application to Equals Money, an FCA-regulated payment institution. SCM is acting as Programme Partner.

YOUR ROLE:
You are building a complete, accurate file that SCM will use to refer this client to Equals Money. Be warm, professional and thorough. You are on the client's side — you want their application to succeed. Ask one question at a time in a natural conversational way. Never use compliance jargon with the client.

ENTITY TYPE — DETERMINE FIRST:
Before collecting anything else, understand what type of entity this is. This drives everything that follows.
- GENERAL CORPORATE: Any standard limited company — trading, tech, professional services, import/export, consultancy, real estate, retail, manufacturing, logistics, media, etc. The vast majority of Equals clients are general corporates.
- REGULATED FINANCIAL SERVICES: VASP/crypto, MSB, investment fund, regulated broker (CFD/spot), asset manager, family office, prop trading firm. These have specific Equals EDD requirements.
- INDUSTRY-SPECIFIC HIGH RISK: Certain industries Equals treats as elevated regardless of FS status — luxury yachts, maritime shipping, jewellery/diamonds/gold, adult content, military/defence, weapons, CBD, vaping, mining, art dealers, aviation, SEO/affiliate marketing, portable electronics. These have their own specific EDD requirements.

EQUALS MONEY — WHAT THEY ACCEPT:
Equals accepts a wide range of corporate clients. The key requirements are:
- Legitimate business with clear, plausible payment purpose
- UBOs and authorised representative identity verified
- Source of funds evidenced
- No involvement with Equals prohibited activities (see below)
- Country risk within appetite (no Prohibited countries)

EQUALS MONEY — PROHIBITED ACTIVITIES (flag clearly in report if identified):
- Anonymous crypto / privacy coins / mixers / tumbling services
- ICO / IEO / token issuance
- Peer-to-peer crypto lending or trading platforms
- Adult entertainment (note: Equals does accept adult content businesses but requires specific EDD — do not refuse, flag for EDD)
- Weapons / firearms (note: Equals does accept with specific EDD — flag for EDD)
- Sanctioned jurisdictions (OFAC/UN/UK/EU prohibited countries)
- Shell companies with no substance and no clear legitimate purpose

STAGE 1 — COMPANY DETAILS:
Collect from ALL clients:
- Full registered company name and any trading names
- Company registration number and date of incorporation
- Registered address and principal place of business (trading address if different)
- VAT number (EU entities only)
- Company website URL (if available)
- Main industry and detailed description of business activity — what they do, how they generate revenue
- Group structure if part of a wider group

LEGAL ENTITY TYPE:
Note whether this is a limited company, partnership, trust, sole trader, fund, or other structure — Equals applies equivalent requirements based on legal form.

STAGE 2 — OWNERSHIP AND CONTROL:
Collect from ALL clients (this is Equals' core KYC requirement):
- All current directors — full names and dates of birth
- All UBOs who own or control 25%+ of share capital or voting rights (directly or indirectly) — full names, nationalities, countries of residence, ownership percentages
- Where ownership is through intermediary entities — trace full chain to the natural person UBOs
- Email address for every director and UBO (needed for identity verification)
- Who is the authorised representative — the main person who will transact with Equals (often a director or authorised signatory)? Collect their name, role, and email.
- Any bearer shares? If yes — flag in report.

STAGE 3 — REGULATORY STATUS:
- Is the company regulated? By which regulator? Licence number and type?
- For MSBs specifically: HMRC MSB registration number (or overseas equivalent) — this is a hard requirement
- For VASPs/Crypto: exact services offered (fiat-to-crypto, OTC, custody, exchange, payments etc.), MiCA status if EU-based, how long operating
- For Gaming: gaming licence jurisdiction and number
- For Investment Funds: which regulated fund manager controls it? Is the fund manager regulated by an approved regulator? (Approved regulators: ASIC, FMA, OSC, BaFin, AMF, AFM, FSMA, CMVM, CBI, FSA Japan, CSSF, Finansinspektionen, FSC Korea, CNMV, FINMA, FCA, SEC)
- For general corporates: being unregulated is normal and not a concern — simply confirm it.

STAGE 4 — AML AND COMPLIANCE:
CRITICAL RULE: Only collect AML/compliance information from entities that are in regulated financial services OR are in an industry Equals treats as elevated risk. Do NOT ask a standard corporate (tech company, importer, consultancy, retailer, logistics firm etc.) for an AML policy — they do not have one and are not required to.

FOR REGULATED FINANCIAL SERVICES ONLY (VASP, MSB, Fund, Broker/CFD/Spot, Asset Manager, Family Office, Prop Trading):
- AML/FinCrime policy — exists? Date last updated? (Equals requires within last 12 months)
- Fraud policy — date last updated? (Equals requires within last 12 months)
- Who is the MLRO? Name, email — are they independent from the board?
- Enterprise-wide risk assessment — MSBs only, this is mandatory
- Client risk assessment template — do they have one?
- Latest external AML/FinCrime audit — when conducted?
- Top 5 clients by transaction size — name, industry, jurisdiction, risk rating (not required for operational flow only cases)
- All regulatory licences/authorisations held — confirm each
- Key beneficiaries (where not their own clients) — who will Equals be sending payments to?
- Third-party inflows — if funds come from third parties into the Equals account (not from the client's own clients), confirm names and purpose
NOTE: AML questionnaire is required for Crypto/VASP, Gaming, MSB, CFD — tell the client SCM will request this from Equals separately, do not ask them to complete it now.

FOR CONSULTANCY BUSINESSES:
- List of customers Equals will receive funds from
- List of key beneficiaries/suppliers Equals will send funds to
- Does their website list all industries they service?
- How do they source clients?
- Customer base breakdown: number, type (B2B/B2C), industry, jurisdictions
- Last 3 months corporate bank statements
- Are they supervised by a local regulator for AML/CFT?

FOR SEO / DIGITAL MARKETING / AFFILIATE MARKETING:
- 3 customer invoices
- Last 3 months corporate bank statements
- Customer base overview: number, type, industry, jurisdictions
- How are customers sourced?
- SEO companies: which platform used to track SEO metrics, screenshots of client dashboards

FOR REAL ESTATE (receiving funds from third parties):
- AML/CFT supervision — are they registered with a local regulator?
- Who owns the properties/land/developments?
- Do they hold client or investor funds? If so, client fund protection arrangements?
- Names of key third parties transferring funds in and purpose
- Source of funds: loan agreement / bank statements / audited accounts / property investment fund prospectus

FOR LUXURY YACHTS / AVIATION / MARITIME:
- Confirmation of all services provided
- Who owns the vessels/aircraft?
- One-off vs recurring clients? Private individuals vs corporates?
- What due diligence do they conduct on clients? Policies and procedures?
- How do they mitigate sanctions risk?
- Do they accept cash payments?

FOR JEWELLERY / DIAMONDS / GOLD / SILVER:
- Registered as high value dealer? If yes — AML policy dated within last 12 months required
- Revenue split: online vs in-person?
- Key suppliers and their geographic locations
- For natural diamonds: Kimberley Process certification confirmation
- Shipping invoices (3 copies, dated within last 3 months)
- Modern Slavery Policy (gold/silver and natural diamonds)

FOR ADULT CONTENT / DATING SERVICES:
- Modern Slavery Policy
- List of all website URLs
- Age verification process documentation
- Content monitoring procedures
- Who provides content? Consent verification process?
- Last 3 months bank statements

FOR MILITARY / DEFENCE / WEAPONS:
- Licence/permit to sell military/defence goods
- Export licence from ECJU if exporting
- Customer base breakdown
- Due diligence on buyers

FOR ART DEALERS:
- HMRC Art Market Participant registration?
- AML policy if registered as high value dealer or AMP
- Accept cash? If yes — cash due diligence process
- Accept crypto? If yes — due diligence on virtual currencies
- Last 3 months bank statements

FOR ALL OTHER GENERAL CORPORATES: Skip Stage 4 entirely. No AML questions.

STAGE 5 — CUSTOMER BASE AND TRANSACTION PROFILE:

FOR REGULATED FINANCIAL SERVICES ENTITIES ONLY:
- Customer base: number of customers, type (business/consumer split), industries, jurisdictions
- Top 5 clients by transaction size (name, industry, jurisdiction, risk rating) — not required for operational flow only
- Do they accept any high-risk client types: gaming, adult entertainment, MSBs, crypto exchanges, shell companies, PEPs?
- Key beneficiaries Equals will be sending funds to — names and geographic locations
- If third-party funds will enter the Equals account (not from their own clients) — confirm names and purpose

FOR INDUSTRY-SPECIFIC ELEVATED RISK (consultancy, SEO, real estate, yachts etc.): Collect customer base info as specified in Stage 4 for that industry.

FOR ALL GENERAL CORPORATES: Do not ask about client base in a financial services sense. Ask only about their suppliers/customers in the context of who Equals will be paying or receiving from.

ALL ENTITY TYPES — TRANSACTION PROFILE (from Equals Product Requirements):
- Estimated annual FX/payment volume required
- Estimated number of payments per annum
- Typical value of individual payments
- Currencies required (including same-currency GBP)
- Annual same-currency requirement
- How will the Equals account be funded? (Named bank account — institution name and country)
- Countries inbound (where funds will be received from)
- Destination of funds (countries outbound)
- Reason/purpose for payments
- Are Equals Money cards needed? (If yes: for employees? Annual spend estimate? Use case? Number of employees? Cards required? ATM withdrawals? Business name on cards?)
- Third-party inflows: will any funds come into the Equals account from parties other than the company's own named account? If yes — collect the names of those parties and the purpose of the payments. This is standard information Equals needs to understand the flow structure. Record accurately in the report — do NOT tell the client this is a problem or a red flag. Third-party receipts between related entities (group companies, fund structures, clients) are entirely normal once Equals understands and approves the flow.

STAGE 6 — SOURCE OF FUNDS:
ALL CLIENTS — provide ONE of the following (source of funds evidence):
1. Latest audited financial statements (preferred)
2. Last 3 months corporate bank statements + 3 customer invoices
3. Unaudited financial statements — ONLY if incorporation date is 5+ years ago AND client is NOT an MSB, Crypto, CFD, or Gaming business

For high-risk private clients (if relevant): bank statement showing accumulation/transfer of relevant funds, or payslips, sale agreements, inheritance docs, loan agreements, investment statements.

Named bank account that will fund the Equals account — institution name and country. This is mandatory for all clients.

STAGE 7 — DOCUMENTS:
ALL CORPORATE CLIENTS — Equals may request any of these to verify company information:
1. Certificate of Incorporation
2. Extract from corporate register / Certificate of Incumbency
3. Memorandum and Articles of Association
4. Register of Directors and Shareholders
5. Source of funds evidence (per Stage 6 above)

REGULATED FINANCIAL SERVICES ENTITIES — additionally:
6. AML/FinCrime policy and procedures (within last 12 months)
7. Fraud policy (within last 12 months)
8. Latest external AML/FinCrime audit report
9. Client risk assessment template
10. Enterprise-wide risk assessment (MSBs only)
11. All regulatory licences/registration certificates
12. Top 5 clients list (name, industry, jurisdiction, risk rating)

DOCUMENT UPLOAD RULE — CRITICAL: When the client confirms a document exists (e.g. "yes we have an AML policy"), immediately ask them to upload it. Do not move on without prompting for the upload. For each regulated FS document confirmed as existing — AML policy, fraud policy, audit report, client risk assessment template, regulatory licence — ask for the upload in the same exchange or the very next message. Do not skip this step. If they cannot upload now, note it as outstanding and move on, but you must ask.

INVESTMENT FUNDS — additionally:
- AML Comfort Letter from administrator (SCM will request the template from Equals)
- PPM/Prospectus/Offering memorandum (if available)
- Constitutional documents / management agreements evidencing GP and Fund Manager
- Certified structure chart (if fund manager not regulated by approved regulator)

INDUSTRY-SPECIFIC ELEVATED RISK — additionally as applicable:
- Consultancy: last 3 months bank statements, 3 customer invoices
- SEO/Digital Marketing: 3 customer invoices, 3 months bank statements
- Real estate: sale/reservation agreement (one-off purchase), relevant SOF docs
- Jewellery/Gold: shipping invoices (3 copies), Modern Slavery Policy, Kimberley Process (diamonds)
- Adult/Dating: Modern Slavery Policy
- Military/Defence/Weapons: licences/permits, export licences
- Art: HMRC AMP registration, AML policy if registered HVD
- Maritime/Aviation: vessel/aircraft ownership docs, shipping manifest
- CBD/Vaping: supplier invoices, bank statements, relevant licences

GENERAL CORPORATES — do NOT ask for AML policy, fraud policy, or any FS-specific EDD documents.

IDENTITY VERIFICATION — Equals requires ID + proof of address for:
- All UBOs (25%+)
- The authorised representative

These will be conducted via Sumsub. Do not ask clients to upload ID — just collect name, role, and email for each person. Note the residence of each person as this determines what ID documents Equals will accept:
- UK residents: passport or UK photo driving licence + utility bill/bank statement/council tax/lease (issued within 3 months)
- EEA residents: passport or national ID card + utility bill/bank statement/tax letter/lease (within 3 months). Non-EEA/UK passport holders also need visa/residence permit.
- Outside UK/EEA: passport/driving licence/national ID + utility bill/bank statement/tax letter/lease (within 3 months). US and UAE nationals also need tax number.

STAGE 8 — SUMMARY AND SUBMIT:
Provide a comprehensive summary of all information collected, clearly organised by section. Before presenting the summary, silently check:
- Any Equals prohibited activity identified?
- Any Prohibited country involvement?
- Any High risk country involvement?
- Third-party inflows present? If yes — confirm names and purpose are documented in the report (Equals will need this to approve the flow structure, not to decline it)
- Named bank account confirmed? (Flag if missing)
- All required documents requested for this entity type?

Present the summary to the client for confirmation. End with:
"If you are happy with everything above, please type **SUBMIT** to send your application to our team."

Do NOT tell the client the outcome of the risk assessment or whether Equals will accept them. Simply confirm the team will be in touch.

EQUALS COUNTRY RISK CLASSIFICATION (silent — classify every country mentioned, never tell client):
Prohibited (1 — outside Equals appetite, must flag): Afghanistan, Belarus, Central African Republic, Crimea/Luhansk/Donetsk/Kherson/Zaporizhzhia, Cuba, North Korea/DPRK, DRC, Gaza, Iran, Iraq, Libya, Mali, Myanmar, Russia, South Sudan, Syria, Venezuela, Yemen
High (2 — EDD required, enhanced scrutiny): Albania, Algeria, Angola, Anguilla, Argentina, Azerbaijan, Bahamas, Bahrain, Bangladesh, Barbados, Benin, Bolivia, Bosnia, Bulgaria, Burkina Faso, Burundi, Cambodia, Cameroon, Cayman Islands, Chad, China, Comoros, Congo (Republic of), Côte d'Ivoire, Croatia, Djibouti, Dominican Republic, Egypt, Equatorial Guinea, Eritrea, Eswatini, Ethiopia, Gabon, Gambia, Ghana, Gibraltar, Guatemala, Guinea, Guinea-Bissau, Haiti, Honduras, Hong Kong, Hungary, India, Jamaica, Kenya, Kosovo, Kyrgyzstan, Lao PDR, Lebanon, Lesotho, Liberia, Madagascar, Marshall Islands, Mauritania, Micronesia, Moldova, Monaco, Montenegro, Mozambique, Namibia, Nauru, Nepal, Nicaragua, Niger, Nigeria, Pakistan, Palau, Palestinian Authority/West Bank, Panama, Papua New Guinea, Paraguay, Philippines, Romania, Sao Tome, Senegal, Serbia, Sierra Leone, Somalia, South Africa, Sri Lanka, Sudan, Suriname, Tajikistan, Tanzania, Thailand, Togo, Trinidad and Tobago, Tunisia, Turkey/Türkiye, Turkmenistan, Turks and Caicos, Uganda, Ukraine, UAE, Uzbekistan, Vanuatu, Vietnam, BVI/Virgin Islands (UK), Western Sahara, Zambia, Zimbabwe
Medium (3 — standard EDD): American Samoa, Antigua and Barbuda, Armenia, Aruba, Belize, Botswana, Brazil, Cape Verde, Colombia, Costa Rica, Curaçao, Cyprus, Czech Republic, Dominica, Ecuador, El Salvador, Estonia, Fiji, Georgia, Greece, Grenada, Guam, Guernsey, Guyana, Indonesia, Isle of Man, Israel, Italy, Jersey, Jordan, Kazakhstan, Kiribati, Kuwait, Latvia, Lithuania, Malawi, Malaysia, Maldives, Malta, Mauritius, Mexico, Mongolia, Montserrat, Morocco, Niue, North Macedonia, Oman, Peru, Poland, Qatar, Rwanda, Saint Kitts, Saint Lucia, Saint Vincent/SVG, Samoa, Saudi Arabia, Seychelles, Sint Maarten, Slovakia, Slovenia, Solomon Islands, Timor Leste, Tonga, Tuvalu, US Virgin Islands
Low (4 — standard KYC): Australia, Austria, Belgium, Bermuda, Brunei, Canada, Chile, Denmark, Finland, France, Germany, Iceland, Ireland, Japan, South Korea, Liechtenstein, Luxembourg, Netherlands, New Zealand, Norway, Portugal, San Marino, Singapore, Spain, Sweden, Switzerland, Taiwan, United Kingdom, United States, Uruguay

For VASPs specifically: Equals only accepts VASPs regulated in Low or Medium risk jurisdictions. A VASP in a High risk jurisdiction requires senior case-by-case review; a VASP in a Prohibited jurisdiction is outside appetite.

REPORT SCHEMA — generate this JSON when client types SUBMIT:
{
  "reportTitle": "...",
  "clientName": "...",
  "contactName": "...",
  "contactEmail": "...",
  "riskTier": "Low/Medium/High",
  "riskRationale": "...",
  "companyDetails": {
    "legalName": "...", "tradingName": "...", "registrationNumber": "...",
    "dateOfIncorporation": "...", "jurisdiction": "...", "registeredAddress": "...",
    "businessDescription": "...", "groupStructure": "..."
  },
  "isDomiciliaryCompany": false,
  "domiciliaryReason": "...",
  "regulatoryStatus": {
    "regulated": false, "regulator": "...", "licenceNumber": "...",
    "licenceType": "...", "licenceStatus": "Active/Pending/Not found", "assessment": "..."
  },
  "entityProfile": {
    "entityType": "VASP/SPOT_BROKER/CFD_BROKER/REGULATED_FUND/ASSET_MANAGER/GAMING/MSB/FAMILY_OFFICE/PROP_TRADING/OTHER",
    "entityTypeNotes": "...",
    "equalsAcceptanceCriteria": "...",
    "eddRequired": true
  },
  "directors": [{"name": "...", "dateOfBirth": "...", "nationality": "...", "role": "...", "email": "..."}],
  "ubos": [{"name": "...", "nationality": "...", "countryOfResidence": "...", "ownership": "...", "email": "..."}],
  "amlPolicyAssessment": {
    "provided": true, "score": "Satisfactory",
    "hasKYCProcedure": true, "hasTransactionMonitoring": true,
    "hasSanctionsScreening": true, "hasMLRO": true,
    "mlroName": "...", "mlroEmail": "...", "mlroIndependent": true,
    "lastReviewed": "...", "hasExternalAudit": false, "lastAuditDate": "...",
    "hasEnterpriseRiskAssessment": false, "hasClientRiskAssessment": false,
    "top5Clients": [{"name": "...", "industry": "...", "jurisdiction": "...", "riskRating": "..."}],
    "notes": "..."
  },
  "sofAssessment": {
    "narrative": "...", "score": "Satisfactory",
    "namedAccount": "...", "fundingRoute": "...",
    "sofDocumentType": "Audited accounts/Bank statements + invoices/Unaudited accounts",
    "notes": "..."
  },
  "bankingProfile": {
    "annualFxVolume": "...", "paymentsPerAnnum": "...", "typicalPaymentSize": "...",
    "currencies": [], "fundingMethod": "...", "inboundCountries": [], "outboundCountries": [],
    "paymentPurpose": "...", "activeClients": "...", "clientSplit": "...",
    "clientCountries": [], "mlroName": "...", "mlroEmail": "...", "mlroIndependent": true,
    "bankingPartners": "...",
    "thirdPartyInbound": false, "thirdPartyInboundDetails": "...",
    "keyBeneficiaries": "...",
    "entitySpecific": {}
  },
  "websiteFindings": {"url": "...", "summary": "...", "redFlags": []},
  "documentsReceived": ["string only"],
  "documentGaps": ["string only"],
  "equalsEddChecklist": {
    "required": true,
    "items": [{"item": "...", "status": "Provided/Outstanding/Not applicable", "notes": "..."}]
  },
  "sumsubRequired": [{"name": "...", "role": "...", "email": "..."}],
  "riskFactors": [{"factor": "...", "rating": "Low/Medium/High", "notes": "..."}],
  "equalsCountryRisk": {
    "entityJurisdiction": {"country": "...", "equalsRating": "Low/Medium/High/Prohibited"},
    "uboResidences": [{"name": "...", "country": "...", "equalsRating": "Low/Medium/High/Prohibited"}],
    "uboNationalities": [{"name": "...", "country": "...", "equalsRating": "Low/Medium/High/Prohibited"}],
    "directorNationalities": [{"name": "...", "country": "...", "equalsRating": "Low/Medium/High/Prohibited"}],
    "worstRating": "Low/Medium/High/Prohibited",
    "hasProhibited": false,
    "hasHigh": false,
    "notes": "..."
  },
  "redFlags": ["string only"],
  "mlroNotes": "..."
}`;

const BANKING_STAGES = ["welcome","company","ownership","regulatory","aml","clients","sof","documents","summary","report"];

export default function BankingApp() {
  const [tokenChecked, setTokenChecked] = useState(false);
  const [token, setToken] = useState(null);
  const [invite, setInvite] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('welcome');
  const [collectedData, setCollectedData] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [consentGiven, setConsentGiven] = useState(false);
  const [equalsTermsAccepted, setEqualsTermsAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const sbHeaders = { 'Content-Type': 'application/json', 'apikey': supabaseKey, 'Authorization': `Bearer ${supabaseKey}` };

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Token check on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) { setError('No invitation token found. Please use the link from your email.'); setTokenChecked(true); return; }
    setToken(t);
    checkToken(t);
  }, []);

  const checkToken = async (t) => {
    try {
      const res = await fetch(`${supabaseUrl}/rest/v1/banking_referral_invites?token=eq.${t}&select=*`, {
        headers: { 'apikey': supabaseKey, 'Authorization': `Bearer ${supabaseKey}` }
      });
      const [inv] = await res.json();
      if (!inv) { setError('Invalid or expired invitation link.'); setTokenChecked(true); return; }

      setInvite(inv);
      const name = inv.contact_name;
      const company = inv.company_name;

      // Update usage count
      await fetch(`${supabaseUrl}/rest/v1/banking_referral_invites?id=eq.${inv.id}`, {
        method: 'PATCH', headers: sbHeaders,
        body: JSON.stringify({ used_count: (inv.used_count || 0) + 1 })
      });

      let sid = inv.session_id;

      if (sid) {
        // Returning client — load existing messages and state
        const { data: sessionData } = await supabase
          .from('banking_referral_sessions')
          .select('collected_data, status, pre_screen, stage')
          .eq('id', sid).single();

        const { data: msgs } = await supabase
          .from('banking_referral_messages')
          .select('*').eq('session_id', sid).order('created_at', { ascending: true });

        const { data: docs } = await supabase
          .from('banking_referral_documents')
          .select('*').eq('session_id', sid);

        if (sessionData?.collected_data) setCollectedData(sessionData.collected_data);
        if (sessionData?.collected_data?.consentConfirmed) setConsentGiven(true);
          if (sessionData?.collected_data?.equalsTermsAccepted) setEqualsTermsAccepted(true);
        if (sessionData?.stage) setStage(sessionData.stage);
        if (docs) setUploadedDocs(docs);
        if (msgs?.length) {
          setMessages(msgs.map(m => ({ role: m.role, content: m.content })));
          sessionIdRef.current = sid;
          setSessionId(sid);
          setTokenChecked(true);
          // Send a returning-client greeting from Alex automatically — don't make them speak first
          const pendingQs = sessionData?.collected_data?.pendingReviewerQuestions || [];
          const hasPendingDocs = sessionData?.status === 'pending_info';
          const returnGreeting = pendingQs.length > 0
            ? `Welcome back${name ? ', ' + name.split(' ')[0] : ''}. I have a few follow-up points for ${company || 'your application'} — let me work through them with you. ${pendingQs[0]}`
            : `Welcome back${name ? ', ' + name.split(' ')[0] : ''}. ${hasPendingDocs ? `I'm here to help you with the outstanding document requests for ${company || 'your application'}. Let's get these sorted — do you have the documents ready to upload?` : `I'm here to continue with your application for ${company || 'your company'}. Shall we pick up where we left off?`}`;
          setMessages(prev => [...prev, { role: 'assistant', content: returnGreeting }]);
          if (sid) {
            await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'assistant', content: returnGreeting });
          }
          return;
        }
      } else {
        // Create new session
        const { data: session } = await supabase
          .from('banking_referral_sessions')
          .insert({ contact_name: name, contact_email: inv.contact_email, company_name: company, status: 'in_progress', stage: 'welcome' })
          .select().single();
        sid = session.id;
        await fetch(`${supabaseUrl}/rest/v1/banking_referral_invites?id=eq.${inv.id}`, {
          method: 'PATCH', headers: sbHeaders, body: JSON.stringify({ session_id: sid })
        });
      }

      sessionIdRef.current = sid;
      setSessionId(sid);
      setCollectedData({ contactName: name, contactEmail: inv.contact_email, prefilled_companyName: company });

      // Welcome message
      const welcome = `Hello ${name.split(' ')[0]},

Thank you for taking the time to speak with me today. I'm Alex, a compliance specialist at Sage Capital Management.

Sage is working on your behalf to prepare your banking application. My role is to make sure we have a complete and well-presented file — think of me as your compliance advocate.

This conversation will take around 30 minutes. I'll guide you through everything we need, one step at a time. Your progress is saved automatically so you can take breaks if you need to.

Before we get started, please confirm you're happy with how we handle your information.`;

      setMessages([{ role: 'assistant', content: welcome }]);
      await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'assistant', content: welcome });
      setTokenChecked(true);

    } catch(e) {
      setError('Failed to load your application: ' + e.message);
      setTokenChecked(true);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading || !consentGiven || !equalsTermsAccepted) return;
    const userMsg = input.trim();
    setInput('');
    setLoading(true);

    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);

    // Save user message
    const sid = sessionIdRef.current;
    if (sid) {
      await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'user', content: userMsg });
    }

    // Check for SUBMIT — fires whenever client types it regardless of stage
    // Alex controls when to ask for SUBMIT, so we trust the client's intent
    const clientTypedSubmit = /^submit[.!?]?$/i.test(userMsg.trim());
    if (clientTypedSubmit) {
      await handleSubmit(newMessages);
      return;
    }

    try {
      // Build system prompt with context
      const scmPrePop = collectedData.prePopulatedData || {};
      const isBankingOnly = scmPrePop.bankingOnlyMode || scmPrePop.scmApproved;
      const collectedSummary = buildCollectedSummary(collectedData);
      const systemWithContext = BANKING_SYSTEM_PROMPT +
        (collectedSummary ? '\n\n============================================================\nALREADY COLLECTED — DO NOT ASK FOR ANY OF THIS AGAIN:\n' + collectedSummary + '\n\nThe above information has already been provided by the client. It is the authoritative record. Even if these topics do not appear in the visible message history (the history may be truncated for long sessions), they have been answered. Never ask for information already listed above.\n============================================================' : '') +
        (isBankingOnly ? `\n\nBANKING TOP-UP MODE — EXISTING SCM CLIENT: This client (${scmPrePop.companyName || collectedData.prefilled_companyName}) is already approved with Sage Capital Management. Their full compliance file is on record. DO NOT collect company details, ownership, regulatory status, AML policy, source of funds or documents — all already done.\n\nFocus ONLY on the banking transaction profile questions: annual FX volume, payment frequency, typical size, currencies, funding method, inbound/outbound countries, payment purpose, client base details, MLRO details, banking partners. Plus entity-specific questions for their type: ${scmPrePop.entityType || 'unknown'}.\n\nGreet them warmly, explain this is a quick follow-up about how they plan to use the account.` : '') +
        `\n\nCURRENT STAGE: ${stage}` +
        `\n\nALL COLLECTED DATA:\n${JSON.stringify(collectedData, null, 2)}` +
        `\n\nUPLOADED DOCUMENTS: ${uploadedDocs.map(d => d.document_name).join(', ') || 'None'}` +
        (collectedData.preScreen ? `\n\nPRE-SCREEN INTELLIGENCE:\n${JSON.stringify(collectedData.preScreen, null, 2).slice(0, 2000)}` : '') +
        (collectedData.briefingNotes ? `\n\nMLRO BRIEFING NOTES — READ CAREFULLY BEFORE PROCEEDING:\n${collectedData.briefingNotes}\n\nThese notes are from the MLRO who set up this session. Respect them fully — if they say certain information is already known, don't ask for it again. If they specify which entity this session covers, base your entire assessment on that entity only. If they ask you to focus on specific areas, prioritise those. These notes are NOT visible to the client — never reference or quote them directly.` : '') +
        `\n\nCRITICAL — BEFORE ASKING ANY QUESTION: Check ALL COLLECTED DATA above first. If a field is already present in the collected data, it has been answered — do NOT ask for it again under any circumstances, even if it is not visible in the truncated message history. The collected data is the authoritative record of everything answered so far. Only ask for information that is genuinely absent from the collected data. One question per message. Warm, natural tone.` +
        (collectedData.pendingReviewerQuestions?.length > 0 ? `\n\nREVIEWER FOLLOW-UP MODE: The reviewer has asked the client to return to address specific points. Work through these naturally one at a time. Do not list them all at once. Be warm and brief — the client has already completed the main interview.\n\nQuestions to address:\n${collectedData.pendingReviewerQuestions.map((q, i) => `${i+1}. ${q}`).join('\n')}` : '') +
        (stage === 'summary' ? `\n\nSUMMARY STAGE: End every response with "If you are happy with everything above, please type **SUBMIT** to send your application to our team."` : '');

      // Cap message history — keep last 50 messages. For long sessions, early messages
      // are captured in collectedData JSON above so nothing is truly lost.
      const cappedMessages = newMessages.slice(-50).map(m => ({ role: m.role, content: m.content }));

      const res = await fetch('/api/banking-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-5',
          max_tokens: stage === 'summary' ? 3000 : 800,
          system: systemWithContext,
          messages: cappedMessages
        })
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error('Banking chat API error:', res.status, errText);
        throw new Error(`API error ${res.status}`);
      }

      const data = await res.json();

      // Handle API-level errors
      if (data.error) {
        console.error('Claude API error:', data.error);
        throw new Error(data.error);
      }

      const reply = data.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

      if (!reply) {
        console.error('Empty reply from Claude. Full response:', JSON.stringify(data).slice(0, 500));
        throw new Error('Empty response from AI');
      }

      const withReply = [...newMessages, { role: 'assistant', content: reply }];
      setMessages(withReply);

      if (sid) {
        await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'assistant', content: reply });
      }

      // Stage progression (simple keyword detection)
      const rl = reply.toLowerCase();
      let newStage = stage;
      if (stage === 'welcome' && (rl.includes('company') || rl.includes('business') || rl.includes('registered'))) newStage = 'company';
      else if (stage === 'company' && (rl.includes('director') || rl.includes('ownership') || rl.includes('ubo') || rl.includes('shareholder'))) newStage = 'ownership';
      else if (stage === 'ownership' && (rl.includes('regulat') || rl.includes('licen') || rl.includes('authoris'))) newStage = 'regulatory';
      else if (stage === 'regulatory' && (rl.includes('aml') || rl.includes('compliance') || rl.includes('mlro') || rl.includes('money laundering'))) newStage = 'aml';
      else if (stage === 'aml' && (rl.includes('client') || rl.includes('investor') || rl.includes('customer') || rl.includes('transaction') || rl.includes('volume'))) newStage = 'clients';
      else if (stage === 'clients' && (rl.includes('source of funds') || rl.includes('bank account') || rl.includes('funded') || rl.includes('audited'))) newStage = 'sof';
      else if (stage === 'sof' && (rl.includes('document') || rl.includes('certificate') || rl.includes('upload') || rl.includes('provide'))) newStage = 'documents';
      else if (stage === 'documents' && (rl.includes('summary') || rl.includes('run through') || rl.includes('confirm') || rl.includes('review everything') || rl.includes('submit'))) newStage = 'summary';
      // Also detect summary stage from Alex asking for SUBMIT
      if (rl.includes('type **submit**') || rl.includes('please type submit') || rl.includes('type submit')) newStage = 'summary';
      if (newStage !== stage) setStage(newStage);

      // Save stage and data
      if (sid) {
        await supabase.from('banking_referral_sessions').update({
          stage: newStage, last_active: new Date().toISOString(),
          collected_data: collectedData
        }).eq('id', sid);
      }

    } catch(e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I apologise — there was a technical issue. Please try again.' }]);
    }
    setLoading(false);
  };

  const handleSubmit = async (currentMessages) => {
    setLoading(true);
    const sid = sessionIdRef.current;

    try {
      // Generate report
      const systemWithContext = BANKING_SYSTEM_PROMPT +
        `\n\nALL COLLECTED DATA:\n${JSON.stringify(collectedData, null, 2)}` +
        `\n\nUPLOADED DOCUMENTS: ${uploadedDocs.map(d => d.document_name).join(', ') || 'None'}`;

      const reportRes = await fetch('/api/banking-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-5',
          max_tokens: 8000,
          system: systemWithContext,
          messages: [
            // Include full conversation so Claude knows what was actually discussed
            ...currentMessages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user', content: 'The client has now submitted. Based on the FULL conversation above and all collected data, generate the complete compliance report JSON now. Use EXACTLY the schema defined in your system instructions. Extract every piece of information the client provided — company details, directors, UBOs, regulatory status, AML details, transaction profile, source of funds. Return ONLY valid JSON, no other text, no markdown fences.' }
          ]
        })
      });

      const reportData = await reportRes.json();
      const reportText = reportData.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '{}';

      let report = {};
      let reportParseError = null;
      try {
        const cleaned = reportText.replace(/```json|```/g, '').trim();
        report = JSON.parse(cleaned);
      } catch(e) {
        reportParseError = e.message;
        console.error('Report parse error:', e);
        // Store raw text as fallback so we don't lose the data
        report = {
          _parseError: true,
          _rawText: reportText.slice(0, 5000),
          clientName: invite?.company_name,
          companyDetails: { legalName: invite?.company_name },
          riskTier: 'Review Required',
          mlroNotes: 'Report generation encountered a parse error — raw data stored. Please review the conversation transcript.',
        };
      }

      // Submit
      await fetch('/api/banking-confirm-submission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sid,
          report,
          collectedData,
          contactName: invite?.contact_name,
          contactEmail: invite?.contact_email,
          companyName: invite?.company_name
        })
      });

      const farewell = `Thank you — your application has been submitted to our team at Sage Capital Management.

We will review your file and be in touch. If we need any additional information we will contact you directly at ${invite?.contact_email}.

If you have any questions in the meantime, please contact us at compliance@sagecapital.co.uk.

Thank you for your time.`;

      setMessages(prev => [...prev, { role: 'assistant', content: farewell }]);
      await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'assistant', content: farewell });
      setSubmitted(true);
      setStage('report');

    } catch(e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'There was an error submitting your application. Please try again or contact compliance@sagecapital.co.uk.' }]);
    }
    setLoading(false);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const sid = sessionIdRef.current;

    for (const file of files) {
      try {
        const filePath = `banking/${sid}/${Date.now()}_${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('onboarding-documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Analyse document with Claude
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        let analysis = { assessment: 'Satisfactory', notes: 'Document received' };
        try {
          const analysisRes = await fetch('/api/banking-chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              model: 'claude-sonnet-5', max_tokens: 800,
              messages: [{
                role: 'user',
                content: [{
                  type: 'document',
                  source: { type: 'base64', media_type: file.type === 'application/pdf' ? 'application/pdf' : 'image/jpeg', data: base64 }
                }, {
                  type: 'text',
                  text: `Assess this document for an Equals Money banking referral application. Return ONLY valid JSON:
{"docType":"exact document type (e.g. Certificate of Incorporation)","assessment":"Satisfactory"|"Needs Review"|"Unsatisfactory","summary":"2 sentence description of what this document is and its key contents","materialFlags":["genuine concerns affecting the application — max 2, empty array if none"],"notedFlags":["minor observations — max 2, empty array if none"],"keyFindings":["key data points: company name, dates, numbers, jurisdictions — max 4"]}`
                }]
              }]
            })
          });
          const ad = await analysisRes.json();
          const at = ad.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '{}';
          analysis = JSON.parse(at.replace(/```json|```/g, '').trim());
        } catch(e) { /* use default */ }

        const { data: doc } = await supabase.from('banking_referral_documents').insert({
          session_id: sid,
          document_name: file.name,
          document_type: analysis.docType || 'Unknown',
          file_path: filePath,
          assessment: analysis.assessment,
          analysis
        }).select().single();

        if (doc) setUploadedDocs(prev => [...prev, doc]);

        // Show upload message immediately, then show reviewing indicator
        const uploadMsg = `I've uploaded: ${file.name}`;
        setMessages(prev => [...prev, { role: 'user', content: uploadMsg }]);
        await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'user', content: uploadMsg });

        // Show reviewing indicator immediately so client knows something is happening
        setLoading(true);
        // Build the full message history including this upload, then ask Alex to respond naturally
        const { data: currentMsgs } = await supabase
          .from('banking_referral_messages')
          .select('role,content').eq('session_id', sid).order('created_at', { ascending: true });

        const { data: sessionInfo } = await supabase
          .from('banking_referral_sessions')
          .select('collected_data,stage').eq('id', sid).single();

        const systemWithContext = BANKING_SYSTEM_PROMPT +
          `\n\nCURRENT STAGE: ${sessionInfo?.stage || stage}` +
          `\n\nALL COLLECTED DATA:\n${JSON.stringify(sessionInfo?.collected_data || collectedData, null, 2)}` +
          `\n\nDOCUMENT JUST RECEIVED: ${file.name} — Assessment: ${analysis.assessment || 'Received'}. ${analysis.notes || ''}` +
          (analysis.assessment !== 'Satisfactory' ? `\n\nNote: This document has issues — acknowledge the specific issue briefly, then ask the client about it or move on to the next required document.` : `\n\nNote: Document is satisfactory — acknowledge briefly then move to the next required document or question.`);

        const history = (currentMsgs || []).map(m => ({ role: m.role, content: m.content }));

        try {
          const ackRes = await fetch('/api/banking-chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              model: 'claude-sonnet-5', max_tokens: 400,
              system: systemWithContext,
              messages: [...history, { role: 'user', content: `I've uploaded: ${file.name}` }]
            })
          });
          const ackData = await ackRes.json();
          const ackMsg = ackData.content?.filter(c => c.type === 'text').map(c => c.text).join('') ||
            `Thank you — I've received ${file.name}. ${analysis.notes || ''}`;
          setMessages(prev => [...prev, { role: 'assistant', content: ackMsg }]);
          await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'assistant', content: ackMsg });
        } catch(e) {
          // Fallback to simple ack if API fails
          const ackMsg = `Thank you — I've received ${file.name}${analysis.notes ? '. ' + analysis.notes : ''}. ${analysis.assessment === 'Satisfactory' ? "That's everything I need for that one." : 'I may need to follow up on this document.'}`;
          setMessages(prev => [...prev, { role: 'assistant', content: ackMsg }]);
          await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'assistant', content: ackMsg });
        }
        setLoading(false);

      } catch(e) {
        console.error('Upload error:', e);
      }
    }
    e.target.value = '';
  };

  const handleConsentChange = async (checked, equalsAlsoChecked) => {
    setConsentGiven(checked);
    const bothAccepted = checked && (equalsAlsoChecked !== undefined ? equalsAlsoChecked : equalsTermsAccepted);
    if (bothAccepted && sessionIdRef.current) {
      const acceptedAt = new Date().toISOString();
      const updatedData = {
        ...collectedData,
        consentConfirmed: true,
        equalsTermsAccepted: true,
        equalsTermsAcceptedAt: acceptedAt,
        equalsTermsVersion: 'Equals Money General Terms and Conditions — Corporate',
        equalsTermsUrl: 'https://www.dropbox.com/scl/fi/4v1vkqo56gkhkvclhtgo4/EQUALS-MONEY-GENERAL-TERMS-AND-CONDITIONS.pdf?rlkey=ad9jwzkay7s14ikz9t3dgm36r&st=9sjp6rm7',
      };
      setCollectedData(updatedData);
      await supabase.from('banking_referral_sessions').update({ collected_data: updatedData }).eq('id', sessionIdRef.current);
      // Send confirmation email with T&Cs copy
      try {
        await fetch('/api/banking-terms-confirmation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contactName: invite?.contact_name,
            contactEmail: invite?.contact_email,
            companyName: invite?.company_name,
            acceptedAt,
            sessionId: sessionIdRef.current,
          })
        });
      } catch(e) { console.error('Terms confirmation email error:', e); }
      // Send first question
      await sendFirstQuestion();
    }
  };

  const handleEqualsTermsChange = async (checked) => {
    setEqualsTermsAccepted(checked);
    if (checked && consentGiven) {
      await handleConsentChange(true, true);
    }
  };

  const sendFirstQuestion = async () => {
    setLoading(true);
    const sid = sessionIdRef.current;
    const name = invite?.contact_name?.split(' ')[0] || 'there';
    const company = invite?.company_name || 'your company';

    try {
      const systemWithContext = BANKING_SYSTEM_PROMPT +
        `\n\nCURRENT STAGE: company` +
        `\n\nALL COLLECTED DATA:\n${JSON.stringify({ ...collectedData, consentConfirmed: true }, null, 2)}` +
        (collectedData.preScreen ? `\n\nPRE-SCREEN INTELLIGENCE:\n${JSON.stringify(collectedData.preScreen, null, 2)}` : '');

      const res = await fetch('/api/banking-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-5', max_tokens: 600,
          system: systemWithContext,
          messages: [{
            role: 'user',
            content: `The client has confirmed consent. Greet ${name} warmly and ask your first question to begin collecting their company details. You already know their name is ${name} and they are from ${company}. Start naturally — confirm the company name and ask about the business.`
          }]
        })
      });

      const data = await res.json();
      const reply = data.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      if (sid) {
        await supabase.from('banking_referral_messages').insert({ session_id: sid, role: 'assistant', content: reply });
        await supabase.from('banking_referral_sessions').update({ stage: 'company' }).eq('id', sid);
      }
      setStage('company');

    } catch(e) {
      console.error('First question error:', e);
    }
    setLoading(false);
  };

  // Stage progress bar
  const stageIndex = BANKING_STAGES.indexOf(stage);
  const progressPct = Math.round((stageIndex / (BANKING_STAGES.length - 1)) * 100);

  if (!tokenChecked) {
    return (
      <div style={{ minHeight:'100vh', background:'#0f0c21', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ color:'#66eddc', fontSize:14, fontFamily:'Arial' }}>Loading your application...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight:'100vh', background:'#0f0c21', display:'flex', alignItems:'center', justifyContent:'center', padding:40 }}>
        <div style={{ background:'#0e152c', border:'1px solid rgba(255,110,123,0.3)', borderRadius:12, padding:32, maxWidth:480, textAlign:'center' }}>
          <div style={{ fontSize:20, color:'#f1f5f9', fontWeight:600, marginBottom:12 }}>Unable to load application</div>
          <div style={{ fontSize:14, color:'rgba(255,255,255,0.5)', marginBottom:20 }}>{error}</div>
          <div style={{ fontSize:13, color:'rgba(255,255,255,0.3)' }}>Contact: <a href="mailto:compliance@sagecapital.co.uk" style={{ color:'#66eddc' }}>compliance@sagecapital.co.uk</a></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:'#0f0c21', display:'flex', flexDirection:'column', fontFamily:'Arial, sans-serif' }}>

      {/* Header */}
      <div style={{ background:'#0e152c', borderBottom:'1px solid rgba(102,237,220,0.15)', padding:'14px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <div style={{ fontSize:10, color:'#66eddc', letterSpacing:'1px', textTransform:'uppercase', marginBottom:2 }}>Sage Capital Management</div>
          <div style={{ fontSize:13, color:'#f1f5f9', fontWeight:600 }}>{invite?.company_name || 'Banking Application'}</div>
        </div>
        <div style={{ fontSize:11, color:'rgba(255,255,255,0.3)' }}>Prepared by Alex · Sage Capital Management</div>
      </div>

      {/* Progress bar */}
      {consentGiven && !submitted && (
        <div style={{ background:'#0e152c', padding:'8px 24px', borderBottom:'1px solid rgba(102,237,220,0.08)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.3)', textTransform:'uppercase', letterSpacing:'0.5px' }}>
              {stage === 'welcome' ? 'Getting started' :
               stage === 'company' ? 'Company details' :
               stage === 'ownership' ? 'Ownership & directors' :
               stage === 'regulatory' ? 'Regulatory status' :
               stage === 'aml' ? 'Compliance & AML' :
               stage === 'clients' ? 'Client base & transactions' :
               stage === 'sof' ? 'Source of funds' :
               stage === 'documents' ? 'Documents' :
               stage === 'summary' ? 'Final review' : 'Complete'}
            </div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.2)' }}>{progressPct}%</div>
          </div>
          <div style={{ height:2, background:'rgba(255,255,255,0.05)', borderRadius:2 }}>
            <div style={{ height:2, background:'#2abfb0', borderRadius:2, width:`${progressPct}%`, transition:'width 0.4s ease' }} />
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex:1, overflowY:'auto', padding:'24px 40px' }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display:'flex', gap:12, marginBottom:20, flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
            {m.role === 'assistant' && (
              <div style={{ width:32, height:32, background:'linear-gradient(135deg,#2abfb0,#66eddc)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'white', flexShrink:0 }}>A</div>
            )}
            <div style={{
              maxWidth:'72%', padding:'12px 16px', borderRadius: m.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
              background: m.role === 'user' ? '#476ae0' : '#0e152c',
              border: m.role === 'user' ? 'none' : '1px solid rgba(102,237,220,0.1)',
              color: m.role === 'user' ? '#fff' : '#e2e8f0',
              fontSize:14, lineHeight:1.7, whiteSpace:'pre-wrap'
            }}>
              {m.content.split('**').map((part, j) =>
                j % 2 === 1 ? <strong key={j}>{part}</strong> : part
              )}
            </div>
          </div>
        ))}

        {/* Consent + Equals T&Cs checkboxes */}
        {(!consentGiven || !equalsTermsAccepted) && messages.length > 0 && (
          <div style={{ background:'rgba(102,237,220,0.05)', border:'1px solid rgba(102,237,220,0.15)', borderRadius:10, padding:'16px 20px', margin:'16px 0', display:'flex', flexDirection:'column', gap:12 }}>
            <label style={{ display:'flex', alignItems:'flex-start', gap:12, cursor:'pointer' }}>
              <input type="checkbox" checked={consentGiven} onChange={e => handleConsentChange(e.target.checked)}
                style={{ marginTop:3, width:16, height:16, cursor:'pointer', flexShrink:0 }} />
              <span style={{ fontSize:13, color:'rgba(255,255,255,0.75)', lineHeight:1.7 }}>
                I confirm I have read and agree to the Sage Capital Management{' '}
                <a href="https://www.sagecapital.co.uk/privacy" target="_blank" rel="noreferrer" style={{ color:'#66eddc' }}>Privacy Policy</a>
                {' '}and understand that the information I provide will be used to prepare a banking referral application on my behalf.
              </span>
            </label>
            <div style={{ borderTop:'1px solid rgba(102,237,220,0.08)', paddingTop:12 }}>
              <label style={{ display:'flex', alignItems:'flex-start', gap:12, cursor:'pointer' }}>
                <input type="checkbox" checked={equalsTermsAccepted} onChange={e => handleEqualsTermsChange(e.target.checked)}
                  style={{ marginTop:3, width:16, height:16, cursor:'pointer', flexShrink:0 }} />
                <span style={{ fontSize:13, color:'rgba(255,255,255,0.75)', lineHeight:1.7 }}>
                  I confirm I have read and accept the{' '}
                  <a href="https://www.dropbox.com/scl/fi/4v1vkqo56gkhkvclhtgo4/EQUALS-MONEY-GENERAL-TERMS-AND-CONDITIONS.pdf?rlkey=ad9jwzkay7s14ikz9t3dgm36r&st=9sjp6rm7" target="_blank" rel="noreferrer" style={{ color:'#66eddc' }}>Equals Money General Terms and Conditions</a>
                  {' '}for the provision of card and payment services. A copy will be emailed to me for my records.
                </span>
              </label>
              {equalsTermsAccepted && !consentGiven && (
                <div style={{ fontSize:11, color:'#f59e0b', marginTop:8, marginLeft:28 }}>Please also tick the Sage Capital Management privacy acknowledgement above to continue.</div>
              )}
            </div>
          </div>
        )}

        {loading && (
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <div style={{ width:32, height:32, background:'linear-gradient(135deg,#2abfb0,#66eddc)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'white' }}>A</div>
            <div style={{ display:'flex', gap:4 }}>
              {[0,1,2].map(d => (
                <div key={d} style={{ width:8, height:8, borderRadius:'50%', background:'#66eddc', opacity:0.6, animation:'pulse 1.2s ease-in-out infinite', animationDelay:`${d*0.2}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {!submitted && (
        <div style={{ padding:'16px 40px 24px', borderTop:'1px solid rgba(102,237,220,0.1)', background:'#0e152c' }}>
          <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
            <button onClick={() => fileInputRef.current?.click()} disabled={!consentGiven || loading}
              style={{ padding:'10px 14px', background: consentGiven ? 'rgba(102,237,220,0.08)' : 'rgba(255,255,255,0.03)', border: consentGiven ? '1px solid rgba(102,237,220,0.35)' : '1px solid rgba(102,237,220,0.1)', borderRadius:8, color: consentGiven ? 'rgba(102,237,220,0.85)' : 'rgba(255,255,255,0.15)', cursor: consentGiven ? 'pointer' : 'not-allowed', fontSize:18, height:44 }}>📎</button>
            <input ref={fileInputRef} type="file" multiple style={{ display:'none' }} onChange={handleFileUpload} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
            <textarea value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if(e.key==='Enter'&&!e.shiftKey&&!loading){e.preventDefault();handleSend();} }}
              placeholder={loading ? 'Alex is responding...' : (consentGiven && equalsTermsAccepted) ? 'Type your response...' : 'Please tick both boxes above to continue...'}
              rows={3} disabled={!consentGiven || !equalsTermsAccepted || loading}
              style={{ flex:1, padding:'10px 14px', background:'rgba(255,255,255,0.04)', border:`1px solid rgba(102,237,220,0.12)`, borderRadius:8, color:'#e2e8f0', fontSize:14, fontFamily:'inherit', resize:'vertical', lineHeight:1.6, minHeight:60, maxHeight:200, opacity: consentGiven && equalsTermsAccepted && !loading ? 1 : 0.4 }} />
            <button onClick={handleSend} disabled={loading||!input.trim()||!consentGiven||!equalsTermsAccepted}
              style={{ padding:'10px 20px', background: loading ? 'rgba(71,106,224,0.4)' : '#476ae0', border:'none', borderRadius:8, color:'white', fontSize:14, fontWeight:600, cursor: consentGiven && equalsTermsAccepted && !loading ? 'pointer' : 'not-allowed', fontFamily:'inherit', height:44, opacity: consentGiven && equalsTermsAccepted && !loading ? 1 : 0.4 }}>Send</button>
          </div>
          {consentGiven && (
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
              <div style={{ fontSize:11, color:'rgba(102,237,220,0.4)' }}>📎 Upload documents — multiple files supported</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.2)' }}>Shift+Enter for new line · Enter to send</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
